const { useCallback } = require('react');

const { useI18n } = require('nordic/i18n');
const { ZIPCODE: ZIPCODE_MLB } = require('@andes/textfield/build/masks/br');
const { ZIPCODE: ZIPCODE_MLM } = require('@andes/textfield/build/masks/mx');

const translate = require('../../../translation');
const ApiService = require('../../../service/api');
const { useFormAddress } = require('./useFormAddress');
const { useFieldContext } = require('../Components/Form/Field');

const ZIPCODE_MLA = {
  mask: 'DDDD',
  tokens: { D: { pattern: /\d/ } },
};

const ZIPCODE_MASK_MAP = {
  MLA: ZIPCODE_MLA,
  MLB: ZIPCODE_MLB,
  MLM: ZIPCODE_MLM,
};

const useAddressActions = () => {
  const { i18n } = useI18n();
  const translations = translate(i18n);
  const { receiverAddress, payer, formConfigs } = useFormAddress();
  const { getFieldsValue, setFieldsValue, setFieldsError, resetFieldsError } =
    useFieldContext();
  const inputs = getFieldsValue();

  const getReusableAddressInfo = useCallback(
    ({ zipcode, stateName, cityName } = {}) =>
      cityName?.length
        ? i18n.gettext('CP {0} - {1}, {2}.', zipcode, stateName, cityName)
        : i18n.gettext('CP {0} - {1}.', zipcode, stateName),
    [],
  );

  const onReuseAddress = useCallback(() => {
    setFieldsValue({
      addressStep: 'reused_address',
      showAddressModal: false,
      isAddressReusable: true,

      street: receiverAddress?.streetName,
      streetNumber: receiverAddress?.streetNumber,
      floor: receiverAddress?.floor,
      zipcode: receiverAddress?.zipcode,
      selectedState: receiverAddress?.stateName,
      selectedCity: receiverAddress?.cityName,
      ...(payer || {}),

      selectedAddress: getReusableAddressInfo(receiverAddress),
    });
  }, []);

  const onModifyAddress = useCallback(() => {
    setFieldsValue({
      addressStep: 'reused_address',
      showAddressModal: false,
      isAddressReusable: true,
      cities: [],
      hasMoreThanOneCity: false,
    });
  }, []);

  const getReusableAddressInfoForModal = useCallback(
    ({ streetName, streetNumber, floor }) =>
      [streetName, streetNumber, floor].join(' '),
    [],
  );

  const transformPayloadAddress = useCallback(
    (data) => ({
      street: data.street,
      street_number: data.streetNumber || 'SN',
      floor: data.floor,
      selected_city: inputs?.selectedCity,
      state: inputs?.selectedState,
      zip_code: inputs?.zipcode,
      residence_option: inputs?.residenceOption || 'residencial',
    }),
    [inputs],
  );

  const createAddressPayload = useCallback(
    (data) => {
      const payload = {
        name: data.name,
        phone_number: data.phoneNumber,
        description: data.description,
        ...transformPayloadAddress(data),
        shipping_action: 'continue',
        ui_components: '',
        address_step: inputs.addressStep,
        cities: inputs.cities,
      };

      if (formConfigs?.email?.show) {
        payload.email = data.email;
      }

      if (formConfigs?.neighborhood?.show) {
        payload.neighborhood = data.neighborhood;
      }

      return payload;
    },
    [inputs],
  );

  const findLocationByZipCode = useCallback(async (zipcode) => {
    try {
      const { locations } = await ApiService.getLocations(zipcode);
      const listCitiesUpdated = locations.map((location) => location.city.name);
      const hasMoreThanOneCity = listCitiesUpdated.length > 1;

      setFieldsValue({
        cities: listCitiesUpdated,
        hasMoreThanOneCity,
        selectedAddress: hasMoreThanOneCity
          ? i18n.gettext('CP {0} - {1}.', zipcode, locations[0]?.state?.name)
          : i18n.gettext(
              'CP {0} - {1}, {2}.',
              zipcode,
              locations[0]?.state?.name,
              locations[0]?.city?.name,
            ),

        selectedCity: listCitiesUpdated[0],
        selectedState: locations[0]?.state?.name,
        zipcode,
      });

      resetFieldsError(['zipcode']);
    } catch (error) {
      setFieldsError({
        zipcode: { message: translations.SHIPPING_ERROR_INVALID_ZIPCODE },
      });
      setFieldsValue({
        cities: [],
        hasMoreThanOneCity: false,
        selectedAddress: '',
        selectedCity: '',
        state: '',
      });
    }
  }, []);

  const isZipCodeValidLen = useCallback(
    ({ zipcode, selectedMaskOptions }) =>
      selectedMaskOptions?.tokens?.D.pattern.test(zipcode) &&
      selectedMaskOptions?.mask?.length === zipcode?.length,
    [],
  );

  const getZipcodeMask = ({ siteId }) => ZIPCODE_MASK_MAP[siteId];

  return {
    onReuseAddress,
    onModifyAddress,
    getReusableAddressInfo,
    getReusableAddressInfoForModal,
    transformPayloadAddress,
    createAddressPayload,
    findLocationByZipCode,
    isZipCodeValidLen,
    getZipcodeMask,
  };
};

module.exports = {
  useAddressActions,
};
